import { useTranslation } from 'react-i18next';

import { getIn } from 'formik';

import { Control, FormCard } from '../../../../components';
import { CARD_CATEGORIES } from '../../../../shared/cardConfig/constants';
import {
  buildSameAsContactFieldArray,
  contactInputEmpty,
  emptyOutFields,
  getValueFromMapping,
  selectWithChildrenContactOnChange
} from '../../../../shared/cardConfig/helpers';
import { CONTACT_FIELDS, SAME_AS_CONTACT_TRANSLATIONS_MAP } from '../../../../shared/fieldConfig/constants';
import { clearFieldError } from '../../../../store/fieldError';
import { CONTROL_TYPE } from '../../../../utils/constants';
import { VALIDATION } from '../../../../utils/validation';
import CreateNew from '../../../CreateNew';
import type { CardDispatch, CardField, CardFieldProps, CardStore } from '../../types';
import CopyShipTo from '../CopyShipTo';
import { checkContactFieldDisable, checkContactSubFields } from '../PackingListContactCard';

const category = CARD_CATEGORIES.orderConfirmation;

function getEmailConfig({ name = '', t }: CardFieldProps) {
  return {
    field: 'email',
    category: name,
    label: t('shipTo.orderConfirmation.email.label'),
    type: CONTROL_TYPE.inputWithTags,
    validations: [
      { type: VALIDATION.array, param: [{ type: VALIDATION.cardEmail }] },
      { type: VALIDATION.maxCharacters, param: 255 }
    ],
    onAddTag: ({ store, dispatch }: { store: CardStore; dispatch: CardDispatch }) => {
      // If there is an error on the parent ('invoice delivery') but an email has been entered, clear the error on the parent
      if (typeof store.fieldError[name] === 'string') {
        dispatch(clearFieldError(name));
      }
    },
    onChange: async (args: Parameters<typeof contactInputEmpty>[0]) => {
      contactInputEmpty(args, name, 'fax', '');
    },
    additionalProps: {
      placeholder: t('shipTo.orderConfirmation.email.placeholder')
    }
  };
}

function getFaxConfig({ name = '', t }: CardFieldProps) {
  return {
    field: 'fax',
    category: name,
    label: t('shipTo.orderConfirmation.fax.label'),
    type: CONTROL_TYPE.text,
    validations: [{ type: VALIDATION.numberOfDigits, param: 10 }],
    onBlur: ({ error, store, dispatch }: { error: unknown; store: CardStore; dispatch: CardDispatch }) => {
      // If there is an error on the parent ('invoiceDelivery') but no error on this field, clear the parent error
      if (!getIn(error, `${category}.fax`) && typeof store.fieldError[name] === 'string') {
        dispatch(clearFieldError(name));
      }
    },
    onChange: async (args: Parameters<typeof contactInputEmpty>[0]) => {
      contactInputEmpty(args, name, 'email', []);
    },
    additionalProps: {
      placeholder: t('shipTo.orderConfirmation.fax.placeholder')
    }
  };
}

function wrap(fieldWrapper?: string, index: number = -1): string | undefined {
  if (fieldWrapper) {
    return index > -1 ? `${fieldWrapper}[${index}].` : `${fieldWrapper}.`;
  }
}

function SelectWithChildren({
  isReview,
  t,
  index = 0,
  fields = [],
  fieldWrapper,
  sameAsFields = [],
  required,
  store
}: CardFieldProps) {
  let wrapper: string | undefined = '';
  // allows for fields to be included on the root level, nested object, or nested array
  wrapper = wrap(fieldWrapper, index);
  const name = `${wrapper}${category}`;

  const controlConfigMap: Record<string, (props: CardFieldProps) => Record<string, unknown>> = {
    [CONTACT_FIELDS.email]: getEmailConfig,
    [CONTACT_FIELDS.fax]: getFaxConfig
  };

  const { showSubFieldError, isNewContact } = checkContactFieldDisable(name, store);
  const validateContactCardSubFileds = checkContactSubFields(category, store);
  const config = {
    category,
    parent: `${wrapper}${category}`,
    disabled: isReview && validateContactCardSubFileds,
    showSubFieldError,
    validateContactCardSubFileds,
    isNewContact,
    name: `${name}.method`,
    contactCardName: `${category}`,
    required: required,
    validations: [{ type: (VALIDATION as Record<string, string>).requiredOrderConfirmation }],
    type: CONTROL_TYPE.selectWithChildren,
    label: t('shipTo.orderConfirmation.method.label'),
    options: [
      { label: t('sameAsContact.none'), value: 'N/A' },
      ...sameAsFields.map((sameAsOption: string) => ({
        label: t(SAME_AS_CONTACT_TRANSLATIONS_MAP[sameAsOption]),
        value: sameAsOption
      })),
      {
        value: t('shipTo.orderConfirmation.newMethod.value'),
        label: t('shipTo.orderConfirmation.newMethod.label'),
        Comp: CreateNew,
        shouldShowChildren: true
      }
    ],
    initialSelectState: ({ customer }: { customer: CardStore }) => {
      const fieldWrapperValue = fieldWrapper ? customer[fieldWrapper] : undefined;
      const rootObj = fieldWrapper
        ? Array.isArray(fieldWrapperValue)
          ? fieldWrapperValue
          : [fieldWrapperValue]
        : customer;
      const orderConfirmation = index > -1 ? rootObj[index][category] : rootObj[category];
      const mapping = {
        ...sameAsFields.reduce((map: Record<string, string>, sameAsField: string) => {
          map[sameAsField] = sameAsField;
          return map;
        }, {}),
        method: orderConfirmation && orderConfirmation.method
      };
      return getValueFromMapping(orderConfirmation, mapping);
    },
    getReviewFieldValue: ({ customer }: { customer: CardStore }) => {
      const rootObj = fieldWrapper ? customer[fieldWrapper] : customer;
      const orderConfirmation = index > -1 ? rootObj[index][category] : rootObj[category];
      const mapping = {
        ...sameAsFields.reduce((map: Record<string, string>, sameAsField: string) => {
          map[sameAsField] = t(SAME_AS_CONTACT_TRANSLATIONS_MAP[sameAsField]);
          return map;
        }, {}),
        method: orderConfirmation && orderConfirmation.method
      };
      return getValueFromMapping(orderConfirmation, mapping);
    },
    onChange: async (value: unknown, dispatch: CardDispatch, formik: unknown) => {
      const [fieldsToUpdate, shouldEmptyEmailAndFax] = buildSameAsContactFieldArray(value, sameAsFields);
      if (shouldEmptyEmailAndFax) {
        await emptyOutFields(
          [
            { name: `${name}.email`, value: [] },
            { name: `${name}.fax`, value: '' }
          ],
          dispatch,
          formik
        );
      }
      await selectWithChildrenContactOnChange({
        dispatch,
        formik,
        fieldsToUpdate,
        category: name
      });
    },
    selectChildren: fields.map((fieldProps: CardField) => {
      const configFunc = controlConfigMap[fieldProps.key];
      return configFunc({ t, name, ...fieldProps });
    })
  };

  return <Control {...config} />;
}

interface OrderConfirmationContactCardProps {
  isReview?: boolean;
  index?: number;
  fields: CardField[];
  fieldWrapper?: string;
  sameAsFields?: string[];
  required?: boolean;
}

function OrderConfirmationContactCard({
  isReview,
  index = 0,
  fields,
  fieldWrapper,
  sameAsFields = [],
  required,
  ...otherProps
}: OrderConfirmationContactCardProps) {
  const { t } = useTranslation();

  const cardProps = {
    cardId: category,
    title: t('shipTo.orderConfirmation.title'),
    subtitle: t('shipTo.orderConfirmation.subtitle'),
    image: '/food/onboarding/assets/Order_Confirmation.svg',
    isReview,
    fieldWrapper,
    t
  };

  return (
    <FormCard {...cardProps}>
      {!isReview && index > -1 && <CopyShipTo index={index} category={category} />}
      <SelectWithChildren
        t={t}
        isReview={isReview}
        index={index}
        fields={fields}
        fieldWrapper={fieldWrapper}
        sameAsFields={sameAsFields}
        required={required}
        {...otherProps}
      />
    </FormCard>
  );
}

export default OrderConfirmationContactCard;
